import type { TripLocationPayload } from "../api";
import { TRAVEL_TYPE_LABEL, TravelType } from "../types";

/** The location answers a trip needs, whichever travel type it is - only the
 *  fields that type uses are shown, validated and sent. `country` is always
 *  the policy country: the one travelled within for Domestic, the
 *  destination for International. Shared by the New Request wizard and the
 *  Request Advance form. */
export interface TripLocationValue {
  country: string;
  originCountry: string;
  startCity: string;
  endCity: string;
}

export const EMPTY_TRIP_LOCATION: TripLocationValue = { country: "", originCountry: "", startCity: "", endCity: "" };

/** True if `value` has at least one letter (any script/language) - used to
 *  reject a city name that's purely digits/symbols (e.g. "2563", typed into
 *  the wrong field). Not a real "is this a valid city" check, just enough to
 *  catch that case; Unicode-aware so a legitimate non-English city name
 *  (e.g. "São Paulo") is never falsely rejected. */
function hasLetters(value: string): boolean {
  return /\p{L}/u.test(value);
}

/** What to tell the employee is still missing from their trip locations, or
 *  null when complete. Mirrors requests_api.py's _clean_trip_locations - the
 *  server is the authority, this just says so before a round trip. */
export function validateTripLocation(travelType: TravelType, loc: TripLocationValue): string | null {
  if (travelType === "domestic") {
    if (!loc.country) return "Select the country you're travelling in.";
    if (!loc.startCity.trim() || !loc.endCity.trim()) return "Enter both the start city and the end city.";
    if (!hasLetters(loc.startCity) || !hasLetters(loc.endCity)) {
      return "City names must contain letters, not just numbers or symbols.";
    }
    return null;
  }
  if (!loc.originCountry) return "Select the starting country.";
  if (!loc.country) return "Select the destination country.";
  if (loc.originCountry === loc.country) return "The starting and destination country must be different.";
  return null;
}

/** The where-to fields to send - only those the travel type uses. */
export function tripLocationPayload(travelType: TravelType, loc: TripLocationValue): TripLocationPayload {
  return travelType === "domestic"
    ? { travelType, country: loc.country, startCity: loc.startCity.trim(), endCity: loc.endCity.trim() }
    : { travelType, country: loc.country, originCountry: loc.originCountry };
}

/** The location fields a travel request or an advance carries - the same
 *  on both (see TripLocationPayload in api.ts). Everything but `country` is
 *  optional because documents raised before the Domestic/International
 *  choice existed have none of it. */
export interface TripLike {
  country: string;
  travelType?: TravelType | null;
  originCountry?: string | null;
  startCity?: string | null;
  endCity?: string | null;
}

/** One line saying where a trip goes: "Pune → Mumbai, India" for a Domestic
 *  trip, "India → Japan" for an International one. Falls back to the bare
 *  country for an older document with no locations, so every table and card
 *  can use this unconditionally. */
export function tripRoute(trip: TripLike): string {
  if (trip.travelType === "domestic" && trip.startCity && trip.endCity) {
    return `${trip.startCity} → ${trip.endCity}, ${trip.country}`;
  }
  if (trip.travelType === "international" && trip.originCountry) {
    return `${trip.originCountry} → ${trip.country}`;
  }
  return trip.country;
}

/** "Domestic" / "International", or null when the document has no type. */
export function tripTypeLabel(trip: TripLike): string | null {
  return trip.travelType ? TRAVEL_TYPE_LABEL[trip.travelType] : null;
}
